import React from 'react';
import { X, Copy } from 'lucide-react';

const copy = (txt) => navigator.clipboard?.writeText(String(txt));

function fmtDateIST(iso) {
  if (!iso) return '—';
  return iso;  // already pre-formatted "YYYY-MM-DD HH:MM IST" from backend
}

const Shell = ({ title, subtitle, onClose, children, testid }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose} data-testid={testid}>
    <div className="relative w-full max-w-xl rounded-2xl border border-gold/40 bg-gradient-to-br from-purple-950 via-bg-primary to-purple-950 p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
      <button data-testid="popup-close-btn" onClick={onClose} className="absolute top-4 right-4 text-text-muted hover:text-gold">
        <X className="w-5 h-5" />
      </button>
      <h3 className="font-heading text-2xl text-gold mb-1">{title}</h3>
      {subtitle && <p className="text-xs text-text-muted mb-4">{subtitle}</p>}
      <div className="space-y-2 max-h-[70vh] overflow-y-auto pr-1">{children}</div>
    </div>
  </div>
);

const Row = ({ label, value, mono, highlight, copyable }) => (
  <div className={`flex items-start justify-between gap-3 rounded-lg px-3 py-2 ${highlight ? 'bg-gold/10 border border-gold/30' : 'bg-white/5 border border-white/10'}`}>
    <div className="text-[11px] uppercase tracking-wider text-text-muted w-1/2">{label}</div>
    <div className={`text-sm flex-1 text-right ${mono ? 'font-mono' : ''} ${highlight ? 'text-gold font-semibold' : 'text-text-main'}`}>{value ?? '—'}</div>
    {copyable && value && (
      <button onClick={() => copy(value)} className="text-text-muted hover:text-gold flex-shrink-0"><Copy className="w-3.5 h-3.5" /></button>
    )}
  </div>
);

const Tag = ({ t }) => <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-purple-900/40 border border-gold/30 text-gold">{t}</span>;

export default function TransitPopup({ planet, data, onClose }) {
  if (!planet) return null;

  // Find next events for this planet
  const t = data?.transits || {};
  const nextSign = (t.next_sign_entries || []).find(e => e.planet_en === planet.name_en);
  const nextNak = (t.next_nakshatra_entries || []).find(e => e.planet_en === planet.name_en);
  const nextPada = (t.next_pada_changes || []).find(e => e.planet_en === planet.name_en);

  return (
    <Shell
      title={`${planet.name_hi} · वर्तमान गोचर`}
      subtitle={`${planet.name_en} · ${planet.sign_hi} · ${planet.dms} · ${planet.nakshatra_hi} पाद ${planet.pada}`}
      onClose={onClose}
      testid="transit-popup"
    >
      {/* Current state */}
      <Row label="राशि" value={planet.sign_hi} highlight />
      <Row label="अंश (DMS)" value={planet.dms} mono copyable highlight />
      <Row label="नक्षत्र" value={planet.nakshatra_hi} />
      <Row label="पाद" value={planet.pada} />
      <Row label="नक्षत्र स्वामी" value={planet.nakshatra_lord_hi} />
      <Row label="दैनिक गति" value={`${planet.speed_deg_per_day}° / ${planet.is_retrograde ? 'वक्री' : 'मार्गी'}`} mono />

      {/* Status badges */}
      <div className="pt-3 pb-1 text-[11px] uppercase tracking-wider text-gold/80">स्थिति</div>
      <div className="flex flex-wrap gap-1.5">
        {planet.is_retrograde && <Tag t="↺ वक्री" />}
        {planet.is_combust && <Tag t="🔥 अस्त" />}
        {planet.is_exalted && <Tag t="↑ उच्च" />}
        {planet.is_debilitated && <Tag t="↓ नीच" />}
        {planet.is_own_sign && <Tag t="⚜ स्वराशि" />}
        {planet.is_mooltrikona && <Tag t="✦ मूलत्रिकोण" />}
        {planet.is_friendly_sign && <Tag t="मित्र राशि" />}
        {planet.is_enemy_sign && <Tag t="शत्रु राशि" />}
        {planet.is_neutral_sign && <Tag t="सम राशि" />}
      </div>

      {/* Upcoming events */}
      <div className="pt-3 pb-1 text-[11px] uppercase tracking-wider text-gold/80">आगामी परिवर्तन</div>
      {nextSign && (
        <Row label={`राशि → ${nextSign.next_sign_hi}`} value={`${fmtDateIST(nextSign.entry_dt_ist)} · ${nextSign.days_until} दिन`} mono />
      )}
      {nextNak && (
        <Row label={`नक्षत्र → ${nextNak.next_nakshatra_hi}`} value={`${fmtDateIST(nextNak.entry_dt_ist)} · ${nextNak.days_until} दिन`} mono />
      )}
      {nextPada && (
        <Row label={`पाद → ${nextPada.next_pada} (${nextPada.next_nakshatra_hi})`} value={`${fmtDateIST(nextPada.entry_dt_ist)} · ${nextPada.days_until} दिन`} mono />
      )}
    </Shell>
  );
}
